"use client";

import {
  MoreVertical,
  ArrowRight,
  ArrowDownToLine,
  ArrowUpFromLine,
  Plus,
  ArrowUp,
} from "lucide-react";
import {
  useLedgerEntries,
  useUserTransactionHistory,
} from "../hooks/query/queries";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import { useEffect, useState } from "react";
import { Skeleton } from "./ui/skeleton";
import TransactionHistoryAll from "./TransactionHistoryAll";
import TransactionHistorySingle from "./TransactionHistorySingle";
import homeIcon from "../assets/images/homeIcon.svg";
import savingsIcon from "../assets/images/savingsProd.svg";
import pendingIcon from "../assets/icons/pending-circle.svg";
import inactive from "../assets/icons/unverified.svg";
import approvedIcon from "../assets/icons/approved-icon.svg";
import CurrencyFormatter from "../utils/currencyFormatter";
import { useNavigate } from "react-router-dom";
import withdrawIcon from "../assets/images/withdrawaIcon.svg";
import buyIcon from "../assets/images/buyIcon.svg";
import sendIcon from "../assets/images/sendIcon.svg";
import zarDeposit from "../assets/images/zarDeposit.svg";
import sellIcon from "../assets/images/sellIcon.svg";
import receiveIcon from "../assets/images/receiveIcon.svg";
import InvestmentHistoryAll from "./InvestmentHistoryAll";
import InvestDateRange from "./InvestDateRange";
import StringFormatter from "../utils/stringFormatter";

export function InvestmentHistory({ ledgerEntries, loadingLedger }) {
  console.log("ledgerEntries", ledgerEntries);
  const [filter, setFilter] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState([]);
  const [openDateRangeModal, setOpenDateRangeModal] = useState(true);
  const [singleTransaction, setSingleTransaction] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [transactionsAll, setTransactionsAll] = useState(false);
  const [isFromAllTr, setIsFromAllTr] = useState(false);
  const [loadingEnt, setLoadingEnt] = useState(true);
  const [dateRange, setDateRange] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const formatter = new CurrencyFormatter();
  const string = new StringFormatter();
  const navigate = useNavigate();

  const handleOpenTHModal = () => {
    setTransactionsAll(true);
  };

  const handleSingleTransaction = (order) => {
    setIsFromAllTr(false);
    setSelectedTransaction(order);
    setSingleTransaction(true);
    setTransactionsAll(false);
  };
  const handleDateRangeClose = ({ startDate, endDate } = {}) => {
    if (startDate && endDate) {
      setCustomStart(startDate);
      setCustomEnd(endDate);
    }
    setDateRange(false);
    setTransactionsAll(true);
  };
  const handleDateRangeOpen = () => {
    setDateRange(true);
    setOpenDateRangeModal(true);
    setTransactionsAll(false);
  };
  return (
    <Card className="col-span-2 flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>My Investments</CardTitle>
      </CardHeader>
      <CardContent className="p-0 flex-1 flex flex-col">
        <div className="space-y-6 p-6 ">
          {loadingLedger ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="grid grid-cols-6 space-x-[60px] py-2 animate-pulse"
              >
                <div className="flex items-center gap-4 col-span-1">
                  <Skeleton className="h-10 w-10 rounded-full bg-gray-300" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-20 bg-gray-300" />
                    <Skeleton className="h-3 w-12 bg-gray-300" />
                  </div>
                </div>

                <div className="flex items-center justify-left col-span-1">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-16 bg-gray-300" />
                    <Skeleton className="h-3 w-20 bg-gray-300" />
                  </div>
                </div>

                <div className="flex items-center justify-left col-span-1">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-16 bg-gray-300" />
                    <Skeleton className="h-3 w-20 bg-gray-300" />
                  </div>
                </div>

                <div className="flex items-center justify-left col-span-1">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-10 bg-gray-300" />
                    <Skeleton className="h-3 w-14 bg-gray-300" />
                  </div>
                </div>

                <div className="flex items-center justify-center col-span-1">
                  <Skeleton className="h-6 w-20 rounded-full bg-gray-300" />
                </div>

                <div className="flex items-center justify-end col-span-1">
                  <Skeleton className="h-10 w-10 rounded-full bg-gray-300" />
                </div>
              </div>
            ))
          ) : ledgerEntries?.length > 0 ? (
            ledgerEntries?.slice(0, 5)?.map((entry, index) => (
              <div key={index} className="grid grid-cols-6 space-x-[60px]">
                <div className="flex items-center gap-4">
                  {entry?.productId?.investmentType === "property" ? (
                    <img src={homeIcon} alt="" className="w-11 h-11" />
                  ) : (
                    <img src={savingsIcon} alt="" className="w-11 h-11" />
                  )}

                  <div>
                    <div className="text-gray-900 font-medium">
                      {entry?.productId?.name}
                    </div>
                    <div className="text-gray-500 text-sm">
                      {string.capitalizeFirst(entry?.productId?.investmentType)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-left">
                  <div>
                    <p className="text-gray-900 font-medium">Value</p>
                    <p className="text-gray-500 text-sm break-words whitespace-normal w-20">
                      {formatter.format(
                        entry?.productId?.value?.$numberDecimal,
                        entry?.productId?.assetId?.asset?.toUpperCase()
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-left">
                  <div>
                    <p className="text-gray-900 font-medium">Risk</p>
                    <p className="text-gray-500 text-sm">
                      {string.capitalizeFirst(entry?.productId?.risk)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-left">
                  <div>
                    <p className="text-gray-900 font-medium">ROI</p>
                    <p className="text-gray-500 text-sm">
                      {entry?.productId?.roi?.$numberDecimal} %
                    </p>
                  </div>
                </div>

                <div className="flex items-center text-xl px-18 justify-center">
                  <div
                    className="flex items-center justify-center px-3.5 py-1 rounded-full"
                    style={{
                      backgroundColor:
                        entry?.productId?.status === "pending"
                          ? "hsl(var(--orange-50))"
                          : entry?.productId?.status === "inactive"
                            ? "hsl(var(--red-50))"
                            : "hsl(var(--green-50))",
                    }}
                  >
                    <img
                      src={
                        entry?.productId?.status === "pending"
                          ? pendingIcon
                          : entry?.productId?.status === "inactive"
                            ? inactive
                            : approvedIcon
                      }
                      alt=""
                      className="mr-2 h-4 w-4"
                    />
                    <p
                      className="text-[14px]"
                      style={{
                        color:
                          entry?.productId?.status === "pending"
                            ? "hsl(var(--orange-700))"
                            : entry?.productId?.status === "inactive"
                              ? "hsl(var(--red-700))"
                              : "hsl(var(--green-700))",
                      }}
                    >
                      {string.capitalizeFirst(entry?.productId?.status)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-end">
                  <Button
                    onClick={() => {
                      navigate("/invest/product", {
                        state: {
                          productType: "asset2",
                          productId: entry?.productId?._id,
                          ledgerId: entry?._id,
                        },
                      });
                    }}
                    variant="outline"
                    size="icon"
                    className="rounded-full"
                  >
                    <ArrowRight className="w-5 h-5 text-gray-500" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="space-y-6 p-6">No investments found</div>
          )}
        </div>

        <Button
          onClick={handleOpenTHModal}
          variant="ghost"
          className="p-8 border-t w-full flex items-center justify-between group rounded-none hover:no-underline"
        >
          View all investments
          <div className="border p-2 rounded-full bg-transparent transition-transform group-hover:translate-x-1">
            <ArrowRight className="h-4 w-4" />
          </div>
        </Button>
        {transactionsAll && !dateRange && (
          <InvestmentHistoryAll
            title={"My Investments"}
            type={"investments"}
            transactionsComplete={ledgerEntries}
            openAll={transactions}
            setOpenAll={setTransactionsAll}
            onOpenSingle={handleSingleTransaction}
            setIsFromAllTr={setIsFromAllTr}
            onDateRangeOpen={handleDateRangeOpen}
            customEnd={customEnd}
            customStart={customStart}
            setCustomStart={setCustomStart}
            setCustomEnd={setCustomEnd}
          />
        )}
        {dateRange && !transactionsAll && (
          <InvestDateRange
            onDateRangeClose={handleDateRangeClose}
            openDateRangeModal={openDateRangeModal}
            setOpenDateRangeModal={setOpenDateRangeModal}
            customEnd={customEnd}
            customStart={customStart}
          />
        )}
      </CardContent>
    </Card>
  );
}
